// Response metadata
interface ResponseMetadata {
  createdAt: number // timestamp
  updatedAt: number // timestamp
  createdBy: string // user id
  status: "draft" | "submitted" | "approved" | "rejected"
  lastModifiedBy?: string // user id
  lastModifiedPlatform?: "web" | "mobile"
  formSnapshotId?: string
}

// Base response value interface
interface BaseResponseValue {
  fieldName: string // reference to field definition name
  type: string // field type
  groupId?: string // present only for dynamic group responses
  instance?: number // present only for dynamic group responses
}

interface FileAttachmentValue {
  id: string
  url: string
  filename: string
  mimeType: string
  size: number
  notes?: string
}

interface FieldMediaAttachmentValue {
  id: string
  url: string
  filename: string
  mimeType: string
  size: number
}

interface FieldMediaTarget {
  fieldId: string
  fieldName: string
  fieldLabel?: string
  groupId?: string
  instance?: number
}

interface FieldMediaEntry {
  target: FieldMediaTarget
  note?: string
  attachments: FieldMediaAttachmentValue[]
}

interface FieldMediaMap {
  [key: string]: FieldMediaEntry
}

// Specific response value types
interface TextResponse extends BaseResponseValue {
  type: "text"
  value: string
}

interface ParagraphResponse extends BaseResponseValue {
  type: "paragraph"
  value: string
}

interface DatePickerResponse extends BaseResponseValue {
  type: "datepicker"
  value: number // timestamp
}

interface OrganizationResponse extends BaseResponseValue {
  type: "organization"
  value: string // organization id
}

interface SelectResponse extends BaseResponseValue {
  type: "select"
  selectionType: "single" | "multi"
  value: string | string[] // single value for single-select, array for multi-select
}

interface BooleanResponse extends BaseResponseValue {
  type: "boolean"
  value: string // "yes" or "no" or custom value
}

interface FileResponse extends BaseResponseValue {
  type: "file"
  value: FileAttachmentValue[]
}

interface SignatureResponse extends BaseResponseValue {
  type: "signature"
  value: Array<{
    name: string
    value: string
  }>
}

interface GeolocationResponse extends BaseResponseValue {
  type: "geolocation"
  value: {
    latitude: number
    longitude: number
  }
}

// Union type for all response values
type ResponseValue =
  | TextResponse
  | ParagraphResponse
  | DatePickerResponse
  | OrganizationResponse
  | SelectResponse
  | BooleanResponse
  | FileResponse
  | SignatureResponse
  | GeolocationResponse

// Response collection - flat structure with dot notation for dynamic groups
// Key format:
// - Regular fields: "fieldId"
// - Dynamic group fields: "groupId.instance.fieldName"
interface Responses {
  [key: string]: ResponseValue
}

interface IncidentDetails {
  lostTime?: boolean
  lostTimeStart?: number | null
  lostTimeEnd?: number | null
  restricted?: boolean
  restrictedStart?: number | null
  restrictedEnd?: number | null
  medicalTreatmentOnly?: boolean
  firstAid?: boolean
}

// Main form response document
interface FormResponse {
  id: string
  formId: string
  formVersion?: string // optional version tracking
  formType: string
  organizationId: string
  organizationEntityId?: string | null
  metadata: ResponseMetadata
  responses: Responses
  fieldMedia?: FieldMediaMap
  incidentDetails?: IncidentDetails
}

type FormResponseWritePayload = Pick<
  FormResponse,
  | "id"
  | "formId"
  | "formType"
  | "organizationId"
  | "organizationEntityId"
  | "responses"
  | "fieldMedia"
  | "incidentDetails"
> & {
  metadata: Pick<
    ResponseMetadata,
    | "createdAt"
    | "updatedAt"
    | "createdBy"
    | "status"
    | "lastModifiedBy"
    | "lastModifiedPlatform"
    | "formSnapshotId"
  >
}

function prepareFormResponseForWrite(formResponse: FormResponse): FormResponseWritePayload {
  const {
    id,
    formId,
    formType,
    organizationId,
    organizationEntityId,
    metadata,
    responses,
    fieldMedia,
    incidentDetails,
  } = formResponse

  return {
    id,
    formId,
    formType,
    organizationId,
    ...(organizationEntityId !== undefined && { organizationEntityId }),
    metadata: {
      createdAt: metadata.createdAt,
      updatedAt: metadata.updatedAt,
      createdBy: metadata.createdBy,
      status: metadata.status,
      ...(metadata.lastModifiedBy !== undefined && {
        lastModifiedBy: metadata.lastModifiedBy,
      }),
      ...(metadata.lastModifiedPlatform !== undefined && {
        lastModifiedPlatform: metadata.lastModifiedPlatform,
      }),
      ...(metadata.formSnapshotId !== undefined && { formSnapshotId: metadata.formSnapshotId }),
    },
    responses,
    ...(fieldMedia !== undefined && { fieldMedia }),
    ...(incidentDetails !== undefined && { incidentDetails }),
  }
}

function buildFieldMediaKey(
  target: Pick<FieldMediaTarget, "fieldId" | "fieldName" | "groupId" | "instance">,
): string {
  if (target.groupId) {
    if (!target.instance) {
      throw new Error("Dynamic-group field media targets require an instance number")
    }

    return `${target.groupId}.${target.instance}.${target.fieldName}`
  }

  return target.fieldId
}

function parseFieldMediaKey(
  key: string,
): { fieldId: string } | { groupId: string; instance: number; fieldName: string } {
  const parts = key.split(".")
  if (parts.length === 3) {
    const instance = Number(parts[1])
    if (!Number.isInteger(instance) || instance <= 0) {
      throw new Error(`Invalid dynamic-group field media key: ${key}`)
    }

    return {
      groupId: parts[0],
      instance,
      fieldName: parts[2],
    }
  }

  return { fieldId: key }
}

function removeFieldMediaEntries(
  fieldMedia: FieldMediaMap | undefined,
  responseKeys: Iterable<string>,
): FieldMediaMap | undefined {
  if (!fieldMedia) {
    return undefined
  }

  const keysToRemove = new Set(responseKeys)
  const remainingEntries = Object.entries(fieldMedia).filter(([key]) => !keysToRemove.has(key))

  return remainingEntries.length > 0 ? Object.fromEntries(remainingEntries) : undefined
}

function reindexFieldMediaAfterDynamicGroupDeletion(
  fieldMedia: FieldMediaMap | undefined,
  groupId: string,
  deletedInstance: number,
  remainingInstances: number[],
): FieldMediaMap | undefined {
  if (!fieldMedia) {
    return undefined
  }

  const reindexedEntries: FieldMediaMap = {}

  Object.values(fieldMedia).forEach((entry) => {
    const { target } = entry

    if (target.groupId !== groupId || !target.instance) {
      reindexedEntries[buildFieldMediaKey(target)] = entry
      return
    }

    if (target.instance === deletedInstance) {
      return
    }

    // Position-compaction using the same surviving-instance map the `responses`
    // reindexer uses, so a row's attachments stay keyed to the same instance as its
    // values. Entries whose instance is not in the map (e.g. a media entry with no
    // matching response key) are left where they are rather than renumbered to 0.
    const positionIndex = remainingInstances.indexOf(target.instance)
    const nextTarget = positionIndex >= 0 ? { ...target, instance: positionIndex + 1 } : target

    reindexedEntries[buildFieldMediaKey(nextTarget)] = {
      ...entry,
      target: nextTarget,
    }
  })

  return Object.keys(reindexedEntries).length > 0 ? reindexedEntries : undefined
}

// Type guard functions
function isStandardResponse(response: ResponseValue): boolean {
  return !("groupId" in response)
}

function isDynamicGroupResponse(response: ResponseValue): boolean {
  return "groupId" in response && "instance" in response
}

function isSelectResponse(response: ResponseValue): response is SelectResponse {
  return response.type === "select"
}

function isBooleanResponse(response: ResponseValue): response is BooleanResponse {
  return response.type === "boolean"
}

export type {
  FieldMediaAttachmentValue,
  FieldMediaEntry,
  FieldMediaMap,
  FieldMediaTarget,
  FileAttachmentValue,
  FormResponse,
  FormResponseWritePayload,
  IncidentDetails,
  ResponseMetadata,
  Responses,
  ResponseValue,
  TextResponse,
  ParagraphResponse,
  DatePickerResponse,
  OrganizationResponse,
  SelectResponse,
  BooleanResponse,
  FileResponse,
  SignatureResponse,
  GeolocationResponse,
}

export {
  buildFieldMediaKey,
  isBooleanResponse,
  isDynamicGroupResponse,
  isSelectResponse,
  isStandardResponse,
  parseFieldMediaKey,
  prepareFormResponseForWrite,
  reindexFieldMediaAfterDynamicGroupDeletion,
  removeFieldMediaEntries,
}

/* example for runtime type checking

const response: ResponseValue = getResponse()

// Use the type guard function
if (isDynamicGroupResponse(response)) {
  // Response has groupId and instance
  console.log(response.groupId)    // ✓ OK (string | undefined)
  console.log(response.instance)   // ✓ OK (number | undefined)
} else {
  // Standard response without groupId/instance
  console.log(response.fieldName)  // ✓ OK
}

*/

/* example for compile-time type narrowing
// If you want to filter or work with only dynamic group responses
function processDynamicGroupResponses(responses: Responses) {
  Object.entries(responses).forEach(([key, response]) => {
    if (isDynamicGroupResponse(response)) {
      // Response has groupId and instance properties
      const { groupId, instance } = response;
      console.log(`Group: ${groupId}, Instance: ${instance}`)
    }
  })
}
*/

/* to handle the dynamic group responses

function getDynamicGroupInstances(responses: Responses, groupName: string): number[] {
  const pattern = new RegExp(`^${groupName}\\.(\\d+)\\.`)
  const instances = new Set<number>()

  Object.keys(responses).forEach(key => {
    const match = key.match(pattern)
    if (match) {
      instances.add(parseInt(match[1]))
    }
  })

  return Array.from(instances).sort((a, b) => a - b)
}

*/
